import { exec } from "js-exec";
import { JSHINT } from "jshint";
import {
  Canvas,
  cast,
  Debugger,
  delayed,
  destroy,
  Device,
  emit,
  Haptics,
  HttpRequest,
  Input,
  Key,
  MouseButton,
  ObjectPool,
  Persistable,
  Random,
  runAllCancellationHandlers,
  Singleton,
  spawn,
  subscribe,
  SURL,
  throttled,
  unsubscribe,
  WebBrowser,
  startAnimationSession
} from "../api/ApiContainer.js";
import { DEFAULT_CONST_AND_MACROS, SAFE_GLOBALS } from "./DefaultContext.js";
import { Logger } from "./Logger.js";
import { ProxyUrl } from "./ProxyUrl.js";
import {
  AIAgentElement,
  AmbientLightElement,
  AudioElement,
  AudioZoneElement,
  BoxColliderElement,
  DirectionalLightElement,
  GroundPlaneElement,
  GroupElement,
  HemisphereLightElement,
  ImageElement,
  LinkElement,
  MediaFrameElement,
  MirrorElement,
  ParticleEmitterElement,
  PDFElement,
  PointLightElement,
  ScriptableObject,
  SkyboxElement,
  SnapColliderElement,
  SpawnerElement,
  SpawnPointElement,
  SpotLightElement,
  TextElement,
  TriggerVolumeElement,
  VideoElement,
  WaterElement,
  WayPointElement,
  ModelElement,
  PrimitiveMeshElement
} from "./ScriptableObjects.js";
import { ScriptableObjects } from "./ScriptableObjects.js";
import { ScriptingPreprocessor } from "./ScriptingPreprocessor.js";
import { SharedContext } from "./SharedContext.js";
import { SlotContext } from "./SlotContext.js";
// duplicate import removed

/**
 *  States
 */
const CleanupState = 0x01;
const StartupState = 0x02;
const UpdateState = 0x04;
const DisposeState = 0x08;

/**
 *
 */
const applyFrameState = (handler, state, once) => {
  let recurrent = false;
  if ((handler?.state & state) == state) {
    recurrent = true;
    if (once) {
      handler.state &= ~state;
    }
  }
  return recurrent;
};

/**
 * @param {*} handler
 * @param {*} action
 * @param {*} delta
 * @param {*} time
 */
const safeAsyncCall = async (handler, action, delta, time) => {
  try {
    if (action instanceof Function) {
      await action(delta, time);
    }
  } catch (e) {
    Logger.error(`${handler.node.name}::${action.name}() ❯ ${e}`);
    handler.state = DisposeState;
  }
};

/**
 *
 * @param {*} hdlr
 * @param {*} pendingEventQueue
 */
const observeSubscriptionsFromEventQueue = async (hdlr, pendingEventQueue = []) => {
  const action = hdlr?.observe;
  try {
    const subscriptions = hdlr.node?.subscriptions;
    if (!(action instanceof Function) || !subscriptions || subscriptions.length == 0 || pendingEventQueue.length == 0)
      return false;

    const subscriptionsEventQueue = pendingEventQueue.filter(evt => subscriptions.has(evt.sender));
    for (const e of subscriptionsEventQueue) {
      await action(e);
    }

    return true;
  } catch (e) {
    Logger.error(`${hdlr.node.name}::${action.name}() ❯ ${e}`);
    hdlr.state = DisposeState;
  }
};

/**
 * Returns the appropriate value for `self` in script code.
 * Wraps into a ScriptableObject only when running in Aframe runtime.
 */
export const wrapSelf = node => {
  if (ScriptableObject?.runtimeMode === "aframe") {
    return ScriptableObjects._wrapObject(node) || node;
  }
  return node;
};

/**
 * LifecycleHandler provides a structured way to register callbacks
 * for different stages at runtime
 */
class LifecycleHandler {
  state = 0;
  node = null;
  startup = () => {};
  update = () => {};
  observe = () => {};
  dispose = () => {};
}

/**
 * Class representing a generic event that can be queued and processed later.
 */
class QueueableEvent {
  static _eventQueueRef = null;
  constructor(sender, data = {}) {
    this.sender = sender;
    this.data = data;
    this.timestamp = Date.now();
  }

  /**
   * Prevent the event from being processed by others.
   */
  stopPropagation() {
    if (!QueueableEvent._eventQueueRef) return;

    const index = this._eventQueueRef.indexOf(this);
    if (index !== -1) {
      this._eventQueueRef.splice(index, 1);
    }
  }
}

/**
 *  ScriptingRuntime
 */
export class ScriptingRuntime {
  /**
   *
   */
  constructor(options = { devMode: false, proxyUrl: null }) {
    this._options = options;
    this._lifecycleHandlers = [];
    this._eventQueue = [];
    this._pendingEventQueue = [];
    // WeakMap that keeps track of which nodes have already been registered and their associated LifecycleHandler.
    // Using a WeakMap ensures we do not create strong references to nodes, allowing them to be garbage-collected
    // when they are removed from the scene graph.
    this._nodeHandlerMap = new WeakMap();

    if (this._options.proxyUrl) {
      ProxyUrl.setProxy(this._options.proxyUrl);
    }

    const { macros, constants } = this.setupEnvironmentVariables(DEFAULT_CONST_AND_MACROS, options);

    this._preprocessor = new ScriptingPreprocessor({
      optimize: this._options.devMode == false,
      macros,
      constants
    });

    this._executionIndexInvalidated = true;
  }

  /**
   * This method creates a new `QueueableEvent` using the provided sender and data,
   * and appends it to the event queue for later processing.
   * @param {string} sender The originator of the event.
   * @param {any} data Optional payload containing relevant details for the event.
   */
  enqueueEvent(sender, data) {
    QueueableEvent._eventQueueRef = this._pendingEventQueue;
    const event = new QueueableEvent(sender, data);
    this._eventQueue.push(event);
    return this;
  }

  /**
   *
   * @param {*} options
   */
  setupEnvironmentVariables(values = { macros: {}, constants: {} }, options) {
    if (options.devMode == true) {
      values.constants.DEBUG = true;
      values.constants.EDITOR = true;
    }

    if (Device.isMobile()) {
      values.constants.IS_MOBILE = true;
    }

    const currentOS = Device.operatingSystem();
    switch (currentOS) {
      case "Windows":
        values.constants.PLATFORM_WINDOWS = true;
        break;
      case "macOS":
        values.constants.PLATFORM_MACOS = true;
        break;
      case "Linux":
        values.constants.PLATFORM_LINUX = true;
        break;
      case "Android":
        values.constants.PLATFORM_ANDROID = true;
        break;
      case "iOS":
        values.constants.PLATFORM_IOS = true;
        break;
    }
    return values;
  }

  /**
   *
   * @param {*} node
   * @param {*} context
   * @returns
   */
  defineAPIGlobals(node, context) {
    const wrappedSelf = wrapSelf(node);
    return {
      self: wrappedSelf,
      cast,
      spawn: spawn(node, this, context),
      destroy: destroy(this),
      delayed,
      throttled,
      emit: emit(this),
      subscribe: subscribe(node),
      unsubscribe: unsubscribe(node),
      startAnimationSession,
      console: Logger,
      Logger,
      Canvas,
      HttpRequest,
      SURL,
      Device,
      WebBrowser,
      Debugger,
      Haptics,
      Key,
      MouseButton,
      Input,
      Random,
      ObjectPool,
      Singleton,
      SharedContext,
      SlotContext,
      Persistable,
      ScriptableObject,
      TextElement,
      GroundPlaneElement,
      ModelElement,
      PrimitiveMeshElement,
      GroupElement,
      SpawnerElement,
      ImageElement,
      AudioElement,
      LinkElement,
      PDFElement,
      VideoElement,
      DirectionalLightElement,
      HemisphereLightElement,
      PointLightElement,
      SpotLightElement,
      SkyboxElement,
      BoxColliderElement,
      ParticleEmitterElement,
      MirrorElement,
      WaterElement,
      SpawnPointElement,
      WayPointElement,
      MediaFrameElement,
      SnapColliderElement,
      TriggerVolumeElement,
      AIAgentElement,
      AudioZoneElement,
      AmbientLightElement
    };
  }

  /**
   * @typedef {Object} BehaviourNodeNode
   * @property {string} id
   * @property {string} code
   * @property {Number} executionIndex
   */

  /**
   * Execute javascript code using lifecycle hooks.
   * @param {BehaviourNodeNode} node
   * @param {*} context
   */
  async registerBehaviourNode(node, context) {
    // Prevent duplicate registration of the exact same node by leveraging the WeakMap.
    // If the node has already been registered we simply return the previously created handler.
    const existingHandler = this._nodeHandlerMap.get(node);
    if (existingHandler) {
      return existingHandler;
    }

    // Forward optional external logger transport without relying on React hooks
    if (context?.loggerTransport && typeof Logger["useTransport"] === "function") {
      Logger["useTransport"](context.loggerTransport);
    }

    const handler = new LifecycleHandler();
    const taskContext = {
      // ---> Context definition
      ...context,
      ...SAFE_GLOBALS,
      // ----> Adding lifecycle control
      lifecycle: (startup, update, observe, dispose) => {
        if (startup) handler.startup = startup;
        if (update) handler.update = update;
        if (observe) handler.observe = observe;
        if (dispose) handler.dispose = dispose;
      },
      // ----> Defines scripting API
      ...this.defineAPIGlobals(node, context)
    };

    const { devMode } = this._options;
    const { code, id } = node;
    const { output } = await this._preprocessor.run(code, id);
    if (devMode) {
      this.runStaticCodeAnalysis(output, taskContext);
    }

    const taskRunner = exec(output);
    taskRunner(taskContext);

    handler.node = node;
    handler.state = StartupState | UpdateState;

    this._lifecycleHandlers.push(handler);
    this._executionIndexInvalidated = true;

    this._nodeHandlerMap.set(node, handler);

    return handler;
  }

  /**
   * Sort nodes
   * @param {*} nodes
   * @returns
   */
  prepareBehaviourNodes(nodes) {
    nodes.sort((a, b) => a.executionIndex - b.executionIndex);
    return nodes;
  }

  /**
   * Compile javascript code using lifecycle hooks.
   * @param {*} node
   * @param {*} context
   */
  async compile(node, context) {
    const taskContext = {
      // ---> Context definition
      ...context,
      ...SAFE_GLOBALS,
      // ----> Adding lifecycle control
      lifecycle: () => {},
      // ----> Defines scripting API
      ...this.defineAPIGlobals(node, context)
    };

    const { code, id } = node;
    // preprocesor actions
    const { output, slots, inspector, editorOnly } = await this._preprocessor.run(code, id);
    // syntax verification
    this.runStaticCodeAnalysis(output, taskContext);

    // compile inspector attributes
    node.EDITOR_HEADER_DESCRIPTION = inspector?.description;
    node.EDITOR_HEADER_TAG = inspector?.tag;

    // compile slots definitions
    const { scene } = context;
    const localSlotContext = {};
    for (const op of slots) {
      localSlotContext[op.target] = SlotContext.connectSlot(node, scene)(op);
    }
    SlotContext.apply(node);

    if (editorOnly && editorOnly !== "") {
      const taskRunner = exec(editorOnly);
      taskRunner({ ...taskContext, ...localSlotContext });
    }
  }

  /**
   * Detects syntax errors, semantic errors, and potential bugs.
   * @param {*} code The input JavaScript source code.
   * @param {*} context Global definitions
   */
  runStaticCodeAnalysis(code, context) {
    const globals = {};
    Object.keys(context).forEach(key => {
      globals[key] = true;
    });

    const jshintOptions = {
      esversion: 11,
      strict: false,
      undef: true,
      unused: false,
      latedef: true,
      varstmt: true,
      nonew: true,
      curly: false,
      leanswitch: true,
      freeze: true,
      loopfunc: true,
      sub: true,
      maxerr: 3,
      globals
    };

    const success = JSHINT(code, jshintOptions);
    if (!success) {
      const { errors } = JSHINT.data();
      const syntaxErrors = errors.map(e => ({
        evidence: e.evidence,
        line: e.line,
        message: e.reason
      }));
      syntaxErrors.sort((a, b) => a.line - b.line);
      const [first] = syntaxErrors;
      const err = new SyntaxError(`${first.message} (line: ${first.line})`);
      err.line = first.line;
      err.evidence = first.evidence?.trimLeft();
      throw err;
    }
  }

  /**
   *  Removes specific node
   */
  unregisterBehaviourNode(node) {
    let handler = this._nodeHandlerMap.get(node);
    if (!handler) {
      handler = this._lifecycleHandlers.find(h => h.node === node || h.node?.id === node?.id);
    }

    if (handler) {
      handler.state = DisposeState;
      this._nodeHandlerMap.delete(node);
    }
  }

  /**
   *  Removes all registered nodes
   */
  unregisterAllBehaviourNodes() {
    runAllCancellationHandlers();

    for (const handler of this._lifecycleHandlers) {
      handler.state = DisposeState;
    }

    this.tick(0, 1);
    this.reset();
  }

  /**
   * Resets the scripting runtime.
   */
  reset() {
    SharedContext.clear();
    this._preprocessor.resetSession();
    this._lifecycleHandlers = [];
    this._executionIndexInvalidated = true;
    this._eventQueue = [];
    this._pendingEventQueue = [];
    this._nodeHandlerMap = new WeakMap();
  }

  /**
   * Executes the life cycle of the behaviors.
   * @param {*} delta
   * @param {*} time
   */
  tick(delta, time) {
    if (this._executionIndexInvalidated) {
      this._lifecycleHandlers.sort((a, b) => a.node.executionIndex - b.node.executionIndex);
      this._executionIndexInvalidated = false;
    }

    if (this._eventQueue.length > 0 || this._pendingEventQueue.length) {
      this._pendingEventQueue = [...this._pendingEventQueue, ...this._eventQueue];
      this._eventQueue.length = 0;
    }

    let eventsFlushed = false;
    for (const hdlr of this._lifecycleHandlers) {
      // <startup> state ⮐
      if (applyFrameState(hdlr, StartupState, true)) {
        safeAsyncCall(hdlr, hdlr.startup);
        continue;
      }

      // <update> state ⮐
      if (applyFrameState(hdlr, UpdateState, false)) {
        safeAsyncCall(hdlr, hdlr.update, delta, time);
      }

      // <observe> state ⮐
      if (observeSubscriptionsFromEventQueue(hdlr, this._pendingEventQueue)) {
        eventsFlushed = true;
      }

      // <dispose> state ⮐
      if (applyFrameState(hdlr, DisposeState, true)) {
        safeAsyncCall(hdlr, hdlr.dispose);
        hdlr.state = CleanupState;
        this.cleanup();
      }
    }

    if (eventsFlushed) {
      this._pendingEventQueue.length = 0;
    }

    Input.eventAllFrameEnd();
  }

  /**
   *  cleanup
   */
  cleanup() {
    this._lifecycleHandlers = this._lifecycleHandlers.filter(h => h.state != CleanupState);
  }
}
